import { useState, useCallback, useEffect, useRef, type Ref, type RefObject } from 'react';

const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
  const isSafari = /safari/.test(userAgent) && !/chrome|chromium|edge/.test(userAgent);
  return isIOSDevice || (isSafari && /macintosh/.test(userAgent));
};

const applyCssFullscreenEnter = (): void => {
  document.body.classList.add('ios-fullscreen');
  document.body.style.overflow = 'hidden';
};

const applyCssFullscreenExit = (): void => {
  document.body.classList.remove('ios-fullscreen');
  document.body.style.overflow = '';
};

const getNativeFullscreenRequest = (element: Element): (() => unknown) | undefined => {
  const h = element as HTMLElement & {
    webkitRequestFullscreen?: () => unknown;
    mozRequestFullScreen?: () => unknown;
    msRequestFullscreen?: () => unknown;
  };
  if (typeof element.requestFullscreen === 'function') {
    return () => element.requestFullscreen();
  }
  if (typeof h.webkitRequestFullscreen === 'function') {
    return () => h.webkitRequestFullscreen!.call(element);
  }
  if (typeof h.mozRequestFullScreen === 'function') {
    return () => h.mozRequestFullScreen!.call(element);
  }
  if (typeof h.msRequestFullscreen === 'function') {
    return () => h.msRequestFullscreen!.call(element);
  }
  return undefined;
};

interface FullscreenHandle {
  active: boolean;
  enter: () => void;
  exit: () => void;
  useCssLayout: boolean;
  rootRef: RefObject<HTMLDivElement | null>;
}

export const useFullscreenHandle = (): FullscreenHandle => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(false);
  const [isCSSMode, setIsCSSMode] = useState(() => isIOS());

  useEffect(() => {
    setIsCSSMode(isIOS());
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setIsCSSMode(isIOS());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const enter = useCallback(() => {
    const enterCss = () => {
      setIsCSSMode(true);
      setActive(true);
      applyCssFullscreenEnter();
    };

    if (isCSSMode) {
      setActive(true);
      applyCssFullscreenEnter();
      return;
    }

    const element = rootRef.current;
    if (!element) {
      enterCss();
      return;
    }

    const requestFn = getNativeFullscreenRequest(element);
    if (!requestFn) {
      enterCss();
      return;
    }

    try {
      const result = requestFn() as Promise<void> | void;
      if (result !== undefined && typeof (result as Promise<void>).then === 'function') {
        (result as Promise<void>).then(() => setActive(true)).catch(enterCss);
      } else {
        setActive(true);
      }
    } catch {
      enterCss();
    }
  }, [isCSSMode]);

  const exit = useCallback(() => {
    if (isCSSMode) {
      setActive(false);
      applyCssFullscreenExit();
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => setActive(false));
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen().then(() => setActive(false));
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen().then(() => setActive(false));
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen().then(() => setActive(false));
      }
    }
  }, [isCSSMode]);

  useEffect(() => {
    if (isCSSMode) return;
    const handleFullscreenChange = () => {
      const isFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setActive(isFullscreen);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [isCSSMode]);

  return { active, enter, exit, useCssLayout: isCSSMode, rootRef };
};

export const FullScreen: React.FC<{ 
  handle: FullscreenHandle; 
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}> = ({ handle, children, className = '', style = {} }) => {
  const fullscreenStyles: React.CSSProperties = {
    ...style,
    ...(handle.active
      ? {
          backgroundColor: '#000',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          ...(handle.useCssLayout
            ? {
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                maxHeight: '100dvh',
                zIndex: 9999,
              }
            : {
                width: '100%',
                height: '100%',
                minHeight: '100%',
              }),
        }
      : {}),
  };
  return (
    <div
      ref={handle.rootRef as Ref<HTMLDivElement>}
      className={`${className} ${handle.active ? 'presentation-fullscreen-root' : ''} ${handle.useCssLayout && handle.active ? 'ios-fullscreen-container' : ''}`}
      style={fullscreenStyles}
    >
      {children}
    </div>
  );
};
